import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getVersion } from "../../services/apiService";
import "../../styles/version-footer.css";

const AUTH_ROUTE_PATHS = new Set(["/login", "/register"]);

/**
 * The deployed build, fetched once.
 *
 * Lives here rather than in the footer's body because the footer is no longer
 * the only place that shows it: on a shell route there is no footer at all and
 * the version reads out of the account menu instead.
 */
export function useAppVersion(enabled = true) {
  const [versionInfo, setVersionInfo] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;

    let isMounted = true;

    const fetchVersion = async () => {
      try {
        const version = await getVersion();
        if (isMounted) setVersionInfo(version);
      } catch (error) {
        // The build label is a nicety. Losing it must never take the chrome
        // that carries it down with it.
        console.error("Failed to fetch version info:", error);
      }
    };

    fetchVersion();

    return () => {
      isMounted = false;
    };
  }, [enabled]);

  return versionInfo;
}

/** `v1.4.0 • abc1234 (main)`, or null when the version has not arrived. */
export function formatAppVersion(versionInfo) {
  if (!versionInfo) return null;
  const branch =
    versionInfo.git?.branch && versionInfo.git.branch !== "HEAD"
      ? ` (${versionInfo.git.branch})`
      : "";
  return `v${versionInfo.version} • ${versionInfo.git?.commit || "dev"}${branch}`;
}

const VersionFooter = ({ forceDisplay = false, variant = "default" }) => {
  const location = useLocation();
  const shouldHide = !forceDisplay && AUTH_ROUTE_PATHS.has(location.pathname);
  const versionInfo = useAppVersion(!shouldHide);
  const versionLabel = formatAppVersion(versionInfo);

  if (shouldHide) {
    return null;
  }

  const footerClassName = ["version-footer"];
  if (variant === "auth") {
    footerClassName.push("version-footer--auth");
  }
  // `compact` is the Workbench strip: a single hairline under the shell so
  // the deployed version stays verifiable without stealing a content row.
  if (variant === "compact") {
    footerClassName.push("version-footer--compact");
  }

  return (
    <footer className={footerClassName.join(" ")}>
      <div className="version-footer__content">
        <p className="version-footer__copyright">
          &copy; {new Date().getFullYear()} CostPilot by Island Millwork. All
          rights reserved.
        </p>
        {versionLabel ? (
          <p className="version-footer__version">{versionLabel}</p>
        ) : null}
      </div>
    </footer>
  );
};

export default VersionFooter;
